import React, { useMemo, useCallback } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Checkbox,
} from "@mui/material";
import colors from "assets/theme-dark/base/colors";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import useSortTable from "../services/useSortTable";

interface ManualUnmatchProps {
  tableData: PhaseTableRow[];
  selectedRows: string[];
  allColumns: { header: string; accessor: string }[];
  loading: boolean;
  error: any;
  unmatchLoading: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  identifier: string;
  handleSelectRow: (fxUniqueId: string) => void;
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUnmatch: () => void;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface PhaseTableRow {
  FX_UNIQUE_ID?: string;
  [key: string]: unknown;
}

const ManualUnmatch: React.FC<ManualUnmatchProps> = ({
  tableData,
  selectedRows,
  allColumns,
  loading,
  error,
  unmatchLoading,
  totalCount,
  page,
  rowsPerPage,
  identifier,
  handleSelectRow,
  handleSelectAll,
  handleUnmatch,
  handleChangePage,
  handleChangeRowsPerPage,
}) => {
  const filteredTableData = useMemo(() => {
    const scoreColumn = `SCORE_${identifier}`;
    return tableData.filter((row) => {
      const scoreValue = row[scoreColumn];
      return scoreValue !== undefined && scoreValue !== null && !!scoreValue;
    });
  }, [tableData, identifier]);

  const setPage = useCallback(
    (page: number) => {
      handleChangePage(null, page);
    },
    [handleChangePage]
  );

  const { sortedRows, sortField, sortDirection, handleSort } = useSortTable(filteredTableData, setPage);

  const memoizedHandleSelectRow = useCallback(
    (fxUniqueId: string) => {
      handleSelectRow(fxUniqueId);
    },
    [handleSelectRow]
  );

  const memoizedHandleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSelectAll(event);
    },
    [handleSelectAll]
  );

  return (
    <MDBox sx={{ overflowX: "auto" }}>
      {loading && <MDTypography>Loading...</MDTypography>}
      {error && <MDTypography color="error">Error: {error.message}</MDTypography>}

      {!loading && !error && sortedRows.length > 0 ? (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: 700 }}>
            <Table aria-label="manual unmatch table" sx={{ minWidth: "max-content" }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={sortedRows.length > 0 && selectedRows.length === sortedRows.length}
                      indeterminate={
                        sortedRows.length > 0 && selectedRows.length > 0 && selectedRows.length < sortedRows.length
                      }
                      onChange={memoizedHandleSelectAll}
                      sx={{
                        color: colors.primary.main,
                        "&.Mui-checked": {
                          color: colors.secondary.main,
                          border: "none",
                        },
                        "&:hover": {
                          color: colors.primary.dark,
                        },
                        border: `1px solid ${colors.primary.main}`,
                        borderRadius: "4px",
                        padding: "0",
                        marginRight: "0.2rem",
                      }}
                    />
                  </TableCell>
                  {allColumns?.map((column) => (
                    <TableCell
                      key={column.accessor}
                      sx={{
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: colors.grey[400],
                        },
                      }}
                      onClick={() => handleSort(column.accessor)}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span>{column.header}</span>
                        {sortField === column.accessor && (
                          <span style={{ marginLeft: 4 }}>
                            {sortDirection === "ASC" ? (
                              <ArrowUpwardIcon fontSize="small" />
                            ) : (
                              <ArrowDownwardIcon fontSize="small" />
                            )}
                          </span>
                        )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows?.map((row, index) => {
                  const fxUniqueId = row.FX_UNIQUE_ID ? String(row.FX_UNIQUE_ID) : `row-${index}`;
                  return (
                    <TableRow key={fxUniqueId}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(fxUniqueId)}
                          onChange={() => memoizedHandleSelectRow(fxUniqueId)}
                          sx={{
                            color: colors.primary.main,
                            "&.Mui-checked": {
                              color: colors.secondary.main,
                              border: "none",
                            },
                            "&:hover": {
                              color: colors.primary.dark,
                            },
                            border: `1px solid ${colors.primary.main}`,
                            borderRadius: "4px",
                            padding: "0",
                            marginRight: "0.2rem",
                          }}
                        />
                      </TableCell>
                      {allColumns?.map((column) => (
                        <TableCell key={`${fxUniqueId}-${column.accessor}`} sx={{ whiteSpace: "nowrap" }}>
                          {String(row[column.accessor] ?? "-")}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 50, 100, 200]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>

          <MDBox mt={2} display="flex" gap={2}>
            <MDButton
              variant="gradient"
              color="error"
              disabled={selectedRows.length === 0 || unmatchLoading}
              onClick={handleUnmatch}
            >
              {unmatchLoading ? "Unmatching..." : `Unmatch Selected (${selectedRows.length})`}
            </MDButton>
          </MDBox>
        </>
      ) : (
        !loading &&
        !error && (
          <MDBox display="flex" justifyContent="center" alignItems="center" mt={20}>
            <MDTypography variant="h3" color="text">
              No data to unmatch
            </MDTypography>
          </MDBox>
        )
      )}
    </MDBox>
  );
};

export default React.memo(ManualUnmatch);
